/**
 * API — información fiscal empresas (Gestión Despacho, fases 1–6).
 */
import api from "@/lib/api";
import { checkSpanishSpelling } from "@/lib/spellcheckApi";

export const FISCAL_INFO_BOARD_PATH = "/despacho/fiscal-info/board";

export const FORBIDDEN_FISCAL_INFO_MESSAGE =
  "No tienes habilitado Gestión Despacho para ver información fiscal.";

export const EMPTY_FISCAL_INFO_MESSAGE =
  "Aún no hay empresas en el catálogo fiscal. Regístrelas en Clientes para gestionar CFS y obligaciones.";

export const LOAD_FISCAL_INFO_ERROR = "No pudimos cargar el tablero de información fiscal.";
export const LOAD_TRACKING_MONTH_ERROR = "No pudimos cargar las obligaciones del mes para captura.";
export const LOAD_FISCAL_DETAIL_ERROR = "No pudimos cargar el detalle fiscal de la empresa.";

export function parseFiscalInfoError(err, { fallbackMessage = LOAD_FISCAL_INFO_ERROR } = {}) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  const extractDetail = () => {
    if (typeof detail === "string" && detail.trim()) return detail.trim();
    if (Array.isArray(detail)) {
      const parts = detail
        .map((row) => row?.msg || row?.message || (typeof row === "string" ? row : ""))
        .filter(Boolean);
      if (parts.length) return parts.join(" ");
    }
    if (detail && typeof detail === "object") {
      const msg = detail.message || detail.msg;
      if (typeof msg === "string" && msg.trim()) return msg.trim();
    }
    return null;
  };

  if (status === 401) {
    return { kind: "error", message: "Sesión expirada. Vuelva a iniciar sesión." };
  }

  if (status === 502 || status === 503 || status === 504) {
    return { kind: "error", message: "El servidor no respondió a tiempo. Intente de nuevo en unos segundos." };
  }

  if (!status && (err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message || "")))) {
    return { kind: "error", message: "La solicitud tardó demasiado. Compruebe la conexión con el servidor." };
  }

  if (!status && err?.message) {
    return { kind: "error", message: String(err.message) };
  }

  if (status === 403) {
    const code = typeof detail === "object" && detail?.code;
    if (code === "DESPACHO_WRITE_FORBIDDEN") {
      return { kind: "write_forbidden", message: "Sin permiso de escritura en Gestión Despacho." };
    }
    if (code === "LICENSE_BLOCKED") {
      const licenseMsg = typeof detail?.message === "string" && detail.message.trim()
        ? detail.message.trim()
        : "La prueba o licencia expiró. Active una licencia en Configuración → Mi Negocio / Licencia.";
      return { kind: "license_blocked", message: licenseMsg };
    }
    if (code === "MODULE_NOT_LICENSED") {
      return { kind: "forbidden", message: FORBIDDEN_FISCAL_INFO_MESSAGE };
    }
    return { kind: "forbidden", message: FORBIDDEN_FISCAL_INFO_MESSAGE };
  }

  if (status === 404) {
    const msg = typeof detail === "string" ? detail : "Recurso no encontrado.";
    return { kind: "not_found", message: msg };
  }

  if (status === 409) {
    const msg = typeof detail === "string" ? detail : "Conflicto al guardar.";
    return { kind: "conflict", message: msg };
  }

  const detailMessage = extractDetail();
  if (detailMessage) {
    return { kind: "error", message: detailMessage };
  }

  return { kind: "error", message: fallbackMessage };
}

function downloadBlobResponse(response, fallbackName) {
  const blob = response.data;
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function createDespachoFiscalInfoApi(httpClient = api) {
  return {
    async getBoard({ autoSyncCsf = false, timeout = autoSyncCsf ? 120000 : 45000 } = {}) {
      const { data } = await httpClient.get(FISCAL_INFO_BOARD_PATH, {
        params: autoSyncCsf ? { auto_sync_csf: true } : undefined,
        timeout,
      });
      return {
        summary: data?.summary || { total: 0, chips: [] },
        groups: Array.isArray(data?.groups) ? data.groups : [],
        autoSyncCsf: data?.auto_sync_csf,
      };
    },

    async getClientDetail(clientId, { autoSyncCsf = false, timeout = autoSyncCsf ? 120000 : 45000 } = {}) {
      const { data } = await httpClient.get(`/despacho/fiscal-info/clients/${clientId}`, {
        params: autoSyncCsf ? { auto_sync_csf: true } : undefined,
        timeout,
      });
      return data;
    },

    async syncCsfInternal(clientId) {
      const { data } = await httpClient.post(
        `/despacho/fiscal-info/clients/${clientId}/sync-csf-internal`,
        null,
        { timeout: 120000 },
      );
      return data;
    },

    async getCsfSyncStatus() {
      const { data } = await httpClient.get("/despacho/fiscal-info/csf-sync-status");
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        summary: data?.summary || { total: 0, with_csf: 0, without_csf: 0, pending_catalog_match: 0 },
      };
    },

    async syncAllCsf() {
      const { data } = await httpClient.post("/despacho/fiscal-info/sync-all-csf");
      return data;
    },

    csfPdfUrl(clientId, { download = false } = {}) {
      const base = httpClient.defaults?.baseURL || "";
      const prefix = base.endsWith("/") ? base.slice(0, -1) : base;
      const q = download ? "?download=true" : "";
      return `${prefix}/despacho/fiscal-info/clients/${encodeURIComponent(clientId)}/csf-pdf${q}`;
    },

    async openCsfPdf(clientId, { download = false } = {}) {
      const response = await httpClient.get(
        `/despacho/fiscal-info/clients/${clientId}/csf-pdf`,
        {
          params: download ? { download: true } : undefined,
          responseType: "blob",
        },
      );
      const blob = response.data;
      const disposition = response.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] || "CFS.pdf";
      const url = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
        return { filename };
      }
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { filename, url };
    },

    async uploadCsf(clientId, file) {
      const form = new FormData();
      form.append("file", file);
      const { data } = await httpClient.post(
        `/despacho/fiscal-info/clients/${clientId}/upload-csf`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return data;
    },

    async listCatalog({ authority, q } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-info/catalog", {
        params: {
          ...(authority && authority !== "all" ? { authority } : {}),
          ...(q ? { q } : {}),
        },
      });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async updateCatalogItem(obligationId, patch) {
      const { data } = await httpClient.put(`/despacho/fiscal-info/catalog/${obligationId}`, patch);
      return data?.item;
    },

    async addClientObligation(clientId, obligationCatalogId, extra = {}) {
      const { data } = await httpClient.post(`/despacho/fiscal-info/clients/${clientId}/obligations`, {
        obligation_catalog_id: obligationCatalogId,
        ...extra,
      });
      return data?.item;
    },

    async patchClientObligation(clientId, obligationRowId, patch) {
      const { data } = await httpClient.patch(
        `/despacho/fiscal-info/clients/${clientId}/obligations/${obligationRowId}`,
        patch,
      );
      return data?.item;
    },

    async confirmCsfSuggested(clientId) {
      const { data } = await httpClient.post(
        `/despacho/fiscal-info/clients/${clientId}/obligations/confirm-csf-suggested`,
      );
      return data;
    },

    async putLaborProfile(clientId, body) {
      const { data } = await httpClient.put(`/despacho/fiscal-info/clients/${clientId}/labor-profile`, body);
      return data;
    },

    async activatePayrollBundle(clientId, body = {}) {
      const { data } = await httpClient.post(
        `/despacho/fiscal-info/clients/${clientId}/obligations/activate-payroll-bundle`,
        body,
      );
      return data;
    },

    async exportBoard({ format = "csv" } = {}) {
      const response = await httpClient.get("/despacho/fiscal-info/export", {
        params: { format },
        responseType: "blob",
      });
      downloadBlobResponse(response, `fiscal_info.${format === "pdf" ? "pdf" : "csv"}`);
    },

    async listTasks(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-tasks/", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async getTask(taskId) {
      const { data } = await httpClient.get(`/despacho/fiscal-tasks/${taskId}`);
      return data;
    },

    async patchTask(taskId, patch) {
      const { data } = await httpClient.patch(`/despacho/fiscal-tasks/${taskId}`, patch);
      return data;
    },

    async getTaskHistory(taskId) {
      const { data } = await httpClient.get(`/despacho/fiscal-tasks/${taskId}/history`);
      return Array.isArray(data?.items) ? data.items : [];
    },

    async listTaskDocumentKinds() {
      const { data } = await httpClient.get("/despacho/fiscal-task-documents/kinds");
      return Array.isArray(data?.items) ? data.items : [];
    },

    async listReadyTaskDocuments(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-task-documents/ready", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async listTaskDocuments(taskId) {
      const { data } = await httpClient.get(`/despacho/fiscal-tasks/${taskId}/documents`);
      return Array.isArray(data?.items) ? data.items : [];
    },

    async uploadTaskDocument(taskId, { file, docKind = "otro", title = "", notes = "", readyForClient = true } = {}) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_kind", docKind);
      fd.append("title", title || "");
      fd.append("notes", notes || "");
      fd.append("ready_for_client", readyForClient ? "true" : "false");
      const { data } = await httpClient.post(`/despacho/fiscal-tasks/${taskId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data?.item;
    },

    taskDocumentDownloadUrl(documentId) {
      return `/api/despacho/fiscal-task-documents/${documentId}/download`;
    },

    async deleteTaskDocument(documentId) {
      const { data } = await httpClient.delete(`/despacho/fiscal-task-documents/${documentId}`);
      return data;
    },

    async previewGenerateTasks(body) {
      const { data } = await httpClient.post("/despacho/fiscal-tasks/preview-generate", body);
      return data;
    },

    async generateTasks(body) {
      const { data } = await httpClient.post("/despacho/fiscal-tasks/generate", body);
      return data;
    },

    async autoSyncFiscalCurrentMonth({ periodKey, timeout = 90000 } = {}) {
      const body = periodKey ? { period_key: periodKey } : {};
      const { data } = await httpClient.post("/despacho/fiscal-tasks/auto-sync-current-month", body, {
        timeout,
      });
      return data;
    },

    async autoSyncFiscalYearToDate({ year, timeout = 180000 } = {}) {
      const { data } = await httpClient.post(
        "/despacho/fiscal-tasks/auto-sync-year-to-date",
        { year },
        { timeout },
      );
      return data;
    },

    async listTasksReportByAuxiliar(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-tasks/reports/by-auxiliar", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async listOverdueTasks() {
      const { data } = await httpClient.get("/despacho/fiscal-tasks/reports/overdue");
      return Array.isArray(data?.items) ? data.items : [];
    },

    async createTask(body) {
      const { data } = await httpClient.post("/despacho/fiscal-tasks/", body);
      return data;
    },

    async listAdHocTemplates() {
      const { data } = await httpClient.get("/despacho/fiscal-tasks/ad-hoc-templates");
      return Array.isArray(data?.items) ? data.items : [];
    },

    async listOperationalTaskRules(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-tasks/operational-rules", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async createOperationalTaskRule(body) {
      const { data } = await httpClient.post("/despacho/fiscal-tasks/operational-rules", body);
      return data;
    },

    async patchOperationalTaskRule(ruleId, patch) {
      const { data } = await httpClient.patch(`/despacho/fiscal-tasks/operational-rules/${ruleId}`, patch);
      return data;
    },

    async deleteOperationalTaskRule(ruleId) {
      const { data } = await httpClient.delete(`/despacho/fiscal-tasks/operational-rules/${ruleId}`);
      return data;
    },

    async previewGenerateInternalTasks(body) {
      const { data } = await httpClient.post("/despacho/fiscal-tasks/preview-generate-internal", body);
      return data;
    },

    async generateInternalTasks(body) {
      const { data } = await httpClient.post("/despacho/fiscal-tasks/generate-internal", body);
      return data;
    },

    async listCalendarEvents(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-calendar/events", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async previewGenerateCompliance(body) {
      const { data } = await httpClient.post("/despacho/fiscal-compliance/preview-generate", body);
      return data;
    },

    async generateCompliance(body) {
      const { data } = await httpClient.post("/despacho/fiscal-compliance/generate", body);
      return data;
    },

    async listComplianceInstances(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/instances", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async getTrackingMatrix({ year, assignedAuxiliarId } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/tracking-matrix", {
        params: {
          year,
          ...(assignedAuxiliarId ? { assigned_auxiliar_id: assignedAuxiliarId } : {}),
        },
      });
      return data;
    },

    async getTrackingPriorPeriods({ year, fiscalClientId, assignedAuxiliarId } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/tracking-matrix/prior-periods", {
        params: {
          year,
          fiscal_client_id: fiscalClientId,
          ...(assignedAuxiliarId ? { assigned_auxiliar_id: assignedAuxiliarId } : {}),
        },
      });
      return data;
    },

    async getTrackingMonthDetail({
      year,
      month,
      fiscalClientId,
      assignedAuxiliarId,
      autoGenerate = true,
      timeout = autoGenerate ? 120000 : 45000,
    } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/tracking-matrix/month-detail", {
        params: {
          year,
          month,
          fiscal_client_id: fiscalClientId,
          auto_generate: autoGenerate,
          ...(assignedAuxiliarId ? { assigned_auxiliar_id: assignedAuxiliarId } : {}),
        },
        timeout,
      });
      return data;
    },

    async putMonthHonorarios(body) {
      const { data } = await httpClient.put("/despacho/fiscal-compliance/month-honorarios", body);
      return data?.item;
    },

    async getTaxSummaryWorkbook({ fiscalClientId, year } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/tax-summary-workbook", {
        params: {
          fiscal_client_id: fiscalClientId,
          year,
        },
      });
      return data;
    },

    async getTaxSummaryCfdiDetail({
      fiscalClientId,
      year,
      rowId,
      monthKey = "all",
      cellSource = "",
    } = {}) {
      const { data } = await httpClient.get(
        "/despacho/fiscal-compliance/tax-summary-workbook/cfdi-detail",
        {
          params: {
            fiscal_client_id: fiscalClientId,
            year,
            row_id: rowId,
            month_key: monthKey || "all",
            cell_source: cellSource || "",
          },
        },
      );
      return data;
    },

    async getTaxSummaryRetencionesInforme({
      fiscalClientId,
      year,
      side = "a_favor",
      monthKey = "all",
    } = {}) {
      const { data } = await httpClient.get(
        "/despacho/fiscal-compliance/tax-summary-workbook/retenciones-informe",
        {
          params: {
            fiscal_client_id: fiscalClientId,
            year,
            side,
            month_key: monthKey || "all",
          },
        },
      );
      return data;
    },

    async saveTaxSummaryWorkbook({
      fiscalClientId,
      year,
      grid,
      workbook,
      commitMonths = false,
    } = {}) {
      const { data } = await httpClient.put("/despacho/fiscal-compliance/tax-summary-workbook", {
        fiscal_client_id: fiscalClientId,
        year,
        grid,
        workbook,
        commit_months: Boolean(commitMonths),
      });
      return data;
    },

    async recalculateTaxSummaryWorkbook({
      fiscalClientId,
      year,
      workbook,
      forceResicoIvaBases = false,
    } = {}) {
      const { data } = await httpClient.post("/despacho/fiscal-compliance/tax-summary-workbook/recalculate", {
        fiscal_client_id: fiscalClientId,
        year,
        workbook,
        force_resico_iva_bases: Boolean(forceResicoIvaBases),
      });
      return data;
    },

    async prefillTaxSummaryFromCfdi({ fiscalClientId, year, overwrite = false } = {}) {
      const { data } = await httpClient.post(
        "/despacho/fiscal-compliance/tax-summary-workbook/prefill-from-cfdi",
        null,
        {
          params: {
            fiscal_client_id: fiscalClientId,
            year,
            overwrite,
          },
          timeout: 120000,
        },
      );
      return data;
    },

    async downloadTaxSummaryWorkbook({ fiscalClientId, year } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/tax-summary-workbook/download", {
        params: {
          fiscal_client_id: fiscalClientId,
          year,
        },
        responseType: "blob",
        timeout: 120000,
      });
      return data;
    },

    async downloadTaxSummaryCfdiDetail({
      fiscalClientId,
      year,
      rowId,
      monthKey = "all",
      cellSource = "",
    } = {}) {
      const { data } = await httpClient.get(
        "/despacho/fiscal-compliance/tax-summary-workbook/cfdi-detail/download",
        {
          params: {
            fiscal_client_id: fiscalClientId,
            year,
            row_id: rowId,
            month_key: monthKey || "all",
            cell_source: cellSource || "",
          },
          responseType: "blob",
          timeout: 120000,
        },
      );
      return data;
    },

    async getTaxSummaryMonthClosures({ fiscalClientId, year, monthKey = "" } = {}) {
      const { data } = await httpClient.get(
        "/despacho/fiscal-compliance/tax-summary-workbook/month-closures",
        {
          params: {
            fiscal_client_id: fiscalClientId,
            year,
            ...(monthKey ? { month_key: monthKey } : {}),
          },
        },
      );
      return data;
    },

    async closeTaxSummaryMonth({ fiscalClientId, year, monthKey, observation = "" } = {}) {
      const { data } = await httpClient.post(
        "/despacho/fiscal-compliance/tax-summary-workbook/month-closures/close",
        {
          fiscal_client_id: fiscalClientId,
          year,
          month_key: monthKey,
          observation,
        },
      );
      return data;
    },

    async reopenTaxSummaryMonth({
      fiscalClientId,
      year,
      monthKey,
      observation = "",
      password = "",
    } = {}) {
      const { data } = await httpClient.post(
        "/despacho/fiscal-compliance/tax-summary-workbook/month-closures/reopen",
        {
          fiscal_client_id: fiscalClientId,
          year,
          month_key: monthKey,
          observation,
          password,
        },
      );
      return data;
    },

    async listMonthClientDocumentBuckets() {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/month-client-documents/buckets");
      return data;
    },

    async listMonthClientDocuments({ fiscalClientId, periodKey, bucket } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/month-client-documents", {
        params: {
          fiscal_client_id: fiscalClientId,
          period_key: periodKey,
          ...(bucket ? { bucket } : {}),
        },
      });
      return data;
    },

    async listReadyMonthClientDocuments(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/month-client-documents/ready", {
        params,
      });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async uploadMonthClientDocument({
      file,
      fiscalClientId,
      periodKey,
      bucket = "declaraciones",
      docKind = "",
      title = "",
      notes = "",
      readyForClient = true,
    } = {}) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("fiscal_client_id", fiscalClientId);
      fd.append("period_key", periodKey);
      fd.append("bucket", bucket);
      fd.append("doc_kind", docKind || "");
      fd.append("title", title || "");
      fd.append("notes", notes || "");
      fd.append("ready_for_client", readyForClient ? "true" : "false");
      const { data } = await httpClient.post("/despacho/fiscal-compliance/month-client-documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data?.item;
    },

    monthClientDocumentDownloadUrl(documentId) {
      return `/api/despacho/fiscal-compliance/month-client-documents/${documentId}/download`;
    },

    async deleteMonthClientDocument(documentId) {
      const { data } = await httpClient.delete(
        `/despacho/fiscal-compliance/month-client-documents/${documentId}`,
      );
      return data;
    },

    async getMonthContableChecklist({ fiscalClientId, periodKey } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/month-contable-checklist", {
        params: {
          fiscal_client_id: fiscalClientId,
          period_key: periodKey,
        },
      });
      return data;
    },

    async toggleMonthContableChecklistItem({
      fiscalClientId,
      periodKey,
      catalogItemId,
      included = true,
    } = {}) {
      const { data } = await httpClient.put("/despacho/fiscal-compliance/month-contable-checklist", {
        fiscal_client_id: fiscalClientId,
        period_key: periodKey,
        catalog_item_id: catalogItemId,
        included: Boolean(included),
      });
      return data?.item;
    },

    async createMonthContableCatalogItem({ label, sortOrder } = {}) {
      const { data } = await httpClient.post("/despacho/fiscal-compliance/month-contable-catalog", {
        label,
        ...(sortOrder != null ? { sort_order: sortOrder } : {}),
      });
      return data?.item;
    },

    async updateMonthContableCatalogItem(itemId, { label, sortOrder } = {}) {
      const { data } = await httpClient.put(
        `/despacho/fiscal-compliance/month-contable-catalog/${itemId}`,
        {
          ...(label != null ? { label } : {}),
          ...(sortOrder != null ? { sort_order: sortOrder } : {}),
        },
      );
      return data?.item;
    },

    async deleteMonthContableCatalogItem(itemId) {
      const { data } = await httpClient.delete(
        `/despacho/fiscal-compliance/month-contable-catalog/${itemId}`,
      );
      return data;
    },

    async resetMonthContableCatalogDefaults() {
      const { data } = await httpClient.post(
        "/despacho/fiscal-compliance/month-contable-catalog/reset-defaults",
      );
      return Array.isArray(data?.items) ? data.items : [];
    },

    async listOtrosCargos({ periodKey, onlyPending } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-compliance/otros-cargos", {
        params: {
          ...(periodKey ? { period_key: periodKey } : {}),
          ...(onlyPending ? { only_pending: true } : {}),
        },
      });
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        count: Number(data?.count) || 0,
        pending_count: Number(data?.pending_count) || 0,
        period_key: data?.period_key || null,
      };
    },

    async patchOtrosCargosCobrado(rowId, cobrado) {
      const { data } = await httpClient.patch(
        `/despacho/fiscal-compliance/otros-cargos/${rowId}`,
        { cobrado: Boolean(cobrado) },
      );
      return data?.item;
    },

    async getComplianceInstance(instanceId) {
      const { data } = await httpClient.get(`/despacho/fiscal-compliance/instances/${instanceId}`);
      return data?.item;
    },

    async patchComplianceInstance(instanceId, patch) {
      const { data } = await httpClient.patch(
        `/despacho/fiscal-compliance/instances/${instanceId}`,
        patch,
      );
      return data?.item;
    },

    async listComplianceEvidence(instanceId) {
      const { data } = await httpClient.get(
        `/despacho/fiscal-compliance/instances/${instanceId}/evidence`,
      );
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        count: Number(data?.count) || 0,
      };
    },

    async uploadComplianceEvidence(instanceId, { file, docType, notes, alsoCoverInstanceIds } = {}) {
      const form = new FormData();
      form.append("file", file);
      form.append("doc_type", docType || "provisional_normal");
      if (notes) form.append("notes", notes);
      const covers = Array.isArray(alsoCoverInstanceIds)
        ? alsoCoverInstanceIds.filter(Boolean)
        : [];
      if (covers.length) {
        form.append("also_covers", covers.join(","));
      }
      const { data } = await httpClient.post(
        `/despacho/fiscal-compliance/instances/${instanceId}/evidence`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
      );
      return data?.item;
    },

    async downloadComplianceEvidence(evidenceId) {
      const { data } = await httpClient.get(
        `/despacho/fiscal-compliance/evidence/${evidenceId}/download`,
        { responseType: "blob", timeout: 120000 },
      );
      return data;
    },

    async deleteComplianceEvidence(evidenceId) {
      const { data } = await httpClient.delete(
        `/despacho/fiscal-compliance/evidence/${evidenceId}`,
      );
      return data;
    },

    async getComplianceInstanceHistory(instanceId) {
      const { data } = await httpClient.get(
        `/despacho/fiscal-compliance/instances/${instanceId}/history`,
      );
      return Array.isArray(data?.items) ? data.items : [];
    },

    async listDueDateRules({ authority, q, scope } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-calendar/due-date-rules", {
        params: {
          ...(authority && authority !== "all" ? { authority } : {}),
          ...(q ? { q } : {}),
          ...(scope ? { scope } : {}),
        },
      });
      return {
        items: Array.isArray(data?.items) ? data.items : [],
        globalFallback: data?.global_fallback || { monthly_default_day: 17 },
      };
    },

    async getCalendarConfig() {
      const { data } = await httpClient.get("/despacho/fiscal-calendar/config");
      return data;
    },

    async putCalendarConfig(body) {
      const { data } = await httpClient.put("/despacho/fiscal-calendar/config", body);
      return data?.config;
    },

    async putDueDateRule(obligationCatalogId, body) {
      const { data } = await httpClient.put(
        `/despacho/fiscal-calendar/due-date-rules/${obligationCatalogId}`,
        body,
      );
      return data?.item;
    },

    async patchDueDateRule(obligationCatalogId, body, options = {}) {
      const { data } = await httpClient.patch(
        `/despacho/fiscal-calendar/due-date-rules/${obligationCatalogId}`,
        body,
        { timeout: options.timeout ?? 30000 },
      );
      return data?.item;
    },

    /** Guardado principal — POST evita bloqueos de PUT/PATCH en proxy/CDN. */
    async saveDueDateRule(obligationCatalogId, body, options = {}) {
      const { data } = await httpClient.post(
        `/despacho/fiscal-calendar/due-date-rules/${obligationCatalogId}/save`,
        body,
        { timeout: options.timeout ?? 30000 },
      );
      return data?.item;
    },

    async getDueDateRuleHistory(obligationCatalogId) {
      const { data } = await httpClient.get(
        `/despacho/fiscal-calendar/due-date-rules/${obligationCatalogId}/history`,
      );
      return Array.isArray(data?.items) ? data.items : [];
    },

    async getSatReferenceCatalog(year = 2026) {
      const { data } = await httpClient.get(`/despacho/fiscal-calendar/sat-reference/${year}`);
      return data;
    },

    async validateFiscalDueRules({ year = 2026, authority } = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-calendar/validate-rules", {
        params: {
          year,
          ...(authority && authority !== "all" ? { authority } : {}),
        },
      });
      return data;
    },

    async applySatReferenceUpdates({ year = 2026, obligation_catalog_ids, only_global = true } = {}) {
      const { data } = await httpClient.post("/despacho/fiscal-calendar/apply-reference-updates", {
        year,
        ...(Array.isArray(obligation_catalog_ids) && obligation_catalog_ids.length
          ? { obligation_catalog_ids }
          : {}),
        only_global,
      });
      return data;
    },

    async listHonorarios(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-honorarios/", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async createHonorario(body) {
      const { data } = await httpClient.post("/despacho/fiscal-honorarios/", body);
      return data?.item;
    },

    async listClientMessageDrafts(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-client-messages/", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async getRemindersSummary() {
      const { data } = await httpClient.get("/despacho/fiscal-reminders/summary");
      return data;
    },

    async previewGenerateReminders() {
      const { data } = await httpClient.post("/despacho/fiscal-reminders/preview-generate");
      return data;
    },

    async generateReminders(body) {
      const { data } = await httpClient.post("/despacho/fiscal-reminders/generate", body);
      return data;
    },

    async sendPendingReminders() {
      const { data } = await httpClient.post("/despacho/fiscal-reminders/send-pending");
      return data;
    },

    async preloadScheduledReminders() {
      const { data } = await httpClient.post("/despacho/fiscal-reminders/preload-scheduled");
      return data;
    },

    async getFamilyPreviewBoard() {
      const { data } = await httpClient.get("/despacho/fiscal-reminders/family-preview-board");
      return data;
    },

    async getScheduledRemindersBoard(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-reminders/scheduled-board", { params });
      return data;
    },

    async listReminderBundleGroups() {
      const { data } = await httpClient.get("/despacho/fiscal-reminders/bundle-groups");
      return Array.isArray(data?.items) ? data.items : [];
    },

    async ensureReminderBundleGroupDefaults() {
      const { data } = await httpClient.post("/despacho/fiscal-reminders/bundle-groups/ensure-defaults");
      return data;
    },

    async createReminderBundleGroup(body) {
      const { data } = await httpClient.post("/despacho/fiscal-reminders/bundle-groups", body);
      return data?.item;
    },

    async updateReminderBundleGroup(groupId, body) {
      const { data } = await httpClient.put(`/despacho/fiscal-reminders/bundle-groups/${groupId}`, body);
      return data?.item;
    },

    async deleteReminderBundleGroup(groupId) {
      const { data } = await httpClient.delete(`/despacho/fiscal-reminders/bundle-groups/${groupId}`);
      return data;
    },

    async listOperationalReminderTemplates() {
      const { data } = await httpClient.get("/despacho/fiscal-reminders/operational-templates");
      return Array.isArray(data?.items) ? data.items : [];
    },

    async createOperationalReminderTemplate(body) {
      const { data } = await httpClient.post("/despacho/fiscal-reminders/operational-templates", body);
      return data;
    },

    async updateOperationalReminderTemplate(templateId, body) {
      const { data } = await httpClient.put(
        `/despacho/fiscal-reminders/operational-templates/${templateId}`,
        body,
      );
      return data;
    },

    async deleteOperationalReminderTemplate(templateId) {
      const { data } = await httpClient.delete(
        `/despacho/fiscal-reminders/operational-templates/${templateId}`,
      );
      return data;
    },

    async getAutoAlertSettings() {
      const { data } = await httpClient.get("/despacho/fiscal-reminders/auto-alert-settings");
      return data;
    },

    async putAutoAlertSettings(body) {
      const { data } = await httpClient.put("/despacho/fiscal-reminders/auto-alert-settings", body);
      return data;
    },

    async listFiscalAlerts(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-alerts/", { params });
      return Array.isArray(data?.items) ? data.items : [];
    },

    async patchFiscalAlert(alertId, patch) {
      const { data } = await httpClient.patch(`/despacho/fiscal-alerts/${alertId}`, patch);
      return data?.item;
    },

    async sendInternalReminder(body) {
      const { data } = await httpClient.post("/despacho/fiscal-alerts/internal-reminder", body);
      return data;
    },

    async listMessageTemplates() {
      const { data } = await httpClient.get("/despacho/fiscal-client-messages/templates");
      return Array.isArray(data?.items) ? data.items : [];
    },

    async previewMessageTemplateSendDate(params = {}) {
      const { data } = await httpClient.get("/despacho/fiscal-client-messages/templates/preview-send-date", { params });
      return data;
    },

    async listClientMessageTypes() {
      const { data } = await httpClient.get("/despacho/fiscal-client-messages/types");
      return Array.isArray(data?.items) ? data.items : [];
    },

    async createClientMessageType(body) {
      const { data } = await httpClient.post("/despacho/fiscal-client-messages/types", body);
      return data;
    },

    async putClientMessageType(typeId, body) {
      const { data } = await httpClient.put(`/despacho/fiscal-client-messages/types/${typeId}`, body);
      return data;
    },

    async deleteClientMessageType(typeId) {
      const { data } = await httpClient.delete(`/despacho/fiscal-client-messages/types/${typeId}`);
      return data;
    },

    async spellcheckClientMessage(text) {
      return checkSpanishSpelling(text);
    },

    async createMessageTemplate(body) {
      const { data } = await httpClient.post("/despacho/fiscal-client-messages/templates", body);
      return data?.item;
    },

    async putMessageTemplate(templateId, body) {
      const { data } = await httpClient.put(`/despacho/fiscal-client-messages/templates/${templateId}`, body);
      return data?.item;
    },

    async deleteMessageTemplate(templateId) {
      const { data } = await httpClient.delete(`/despacho/fiscal-client-messages/templates/${templateId}`);
      return data;
    },

    async createCustomCalendarObligation(body) {
      const { data } = await httpClient.post("/despacho/fiscal-calendar/custom-obligations", body);
      return data?.item;
    },

    async deleteCustomCalendarObligation(obligationCatalogId) {
      const { data } = await httpClient.delete(
        `/despacho/fiscal-calendar/custom-obligations/${obligationCatalogId}`,
      );
      return data;
    },

    async createClientMessageDraft(body) {
      const { data } = await httpClient.post("/despacho/fiscal-client-messages/", body);
      return data?.item;
    },

    async patchClientMessageDraft(draftId, patch) {
      const { data } = await httpClient.patch(`/despacho/fiscal-client-messages/${draftId}`, patch);
      return data?.item;
    },
  };
}

export const despachoFiscalInfoApi = createDespachoFiscalInfoApi();
